// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { FileText, Download } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';

interface MediaDisplayComponentProps {
  proxyUrl: string;
  mediaUrl: string;
  isImage: boolean;
  index: number;
  allMediaUrls: string[];
  onOpenModal: (initialIndex: number) => void;
  displayInline?: boolean;
}

export default function MediaDisplayComponent({
  proxyUrl,
  mediaUrl,
  isImage,
  index,
  allMediaUrls,
  onOpenModal,
  displayInline = false
}: MediaDisplayComponentProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isActuallyImage, setIsActuallyImage] = useState(isImage);
  const { selectedBusiness } = useBusiness();
  const lastTouchTime = useRef(0);

  useEffect(() => {
    if (!selectedBusiness?.id) {
      setLoading(false);
      return;
    }

    if (!displayInline && !isImage) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    setImageSrc(null);
    setIsActuallyImage(isImage);

    const fetchImage = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await fetch(proxyUrl, {
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
            ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();
        const contentType = response.headers.get('content-type') || '';
        const actuallyIsImage = contentType.startsWith('image/') || isImage;
        setIsActuallyImage(actuallyIsImage);
        
        if (actuallyIsImage || displayInline) {
          const blobUrl = URL.createObjectURL(blob);
          setImageSrc(blobUrl);
          setError(false);
          setLoading(false);
        } else {
          setError(true);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching media:', err);
        setError(true);
        setLoading(false);
      }
    };

    fetchImage();
  }, [proxyUrl, isImage, selectedBusiness?.id, displayInline]);

  useEffect(() => {
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  const handleTouchEnd = (e: React.TouchEvent) => {
    const now = Date.now();
    if (now - lastTouchTime.current < 300) {
      return;
    }
    lastTouchTime.current = now;
    
    e.preventDefault();
    e.stopPropagation();
    onOpenModal(index);
  };

  const handleClick = (e: React.MouseEvent) => {
    const now = Date.now();
    if (now - lastTouchTime.current < 300) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    onOpenModal(index);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (imageSrc) {
      try {
        const mediaId = mediaUrl.split('/Media/')[1]?.split('/')[0] || `image-${index + 1}`;
        const filename = `photo-${mediaId}.jpg`;

        const link = document.createElement('a');
        link.href = imageSrc;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading image:', error);
        window.open(imageSrc, '_blank');
      }
    }
  };

  const handleOpenAttachment = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(proxyUrl, {
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
          ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      }
    } catch (err) {
      console.error('Error opening attachment:', err);
    }
  };

  if (!isImage && !displayInline) {
    return (
      <a
        href={proxyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 hover:underline text-xs flex items-center space-x-1"
        onClick={handleOpenAttachment}
      >
        <FileText className="h-4 w-4" />
        <span>View attachment {index + 1}</span>
      </a>
    );
  }

  if (displayInline) {
    return (
      <div 
        className="rounded-lg overflow-hidden cursor-pointer max-w-xs sm:max-w-md mt-2"
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        onContextMenu={(e) => e.preventDefault()}
        style={{ 
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        {imageSrc ? (
          <div className="relative group">
            <img
              src={imageSrc}
              alt={`Photo ${index + 1}`}
              className="max-w-full h-auto rounded-lg hover:opacity-90 transition-opacity"
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onError={() => {
                setError(true);
                setLoading(false);
              }}
              onLoad={() => {
                setError(false);
              }}
            />
            <button
              onClick={handleDownload}
              className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
              title="Download photo"
            >
              <Download className="h-4 w-4 text-white" />
            </button>
          </div>
        ) : loading ? (
          <div className="rounded-lg bg-gray-100 p-4 text-center text-xs text-gray-500 animate-pulse">
            <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
              <span>Loading image...</span>
            </div>
          </div>
        ) : error ? (
          <div className="rounded-lg bg-gray-100 p-4 text-center text-xs text-gray-500 border border-gray-300">
            <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>Failed to load image</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLoading(true);
                setError(false);
                const fetchImage = async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    const response = await fetch(proxyUrl, {
                      headers: {
                        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
                        ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
                      }
                    });
                    if (response.ok) {
                      const blob = await response.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      setImageSrc(blobUrl);
                      setLoading(false);
                    }
                  } catch (err) {
                    setError(true);
                    setLoading(false);
                  }
                };
                fetchImage();
              }}
              className="mt-2 text-blue-500 hover:underline text-xs"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="rounded-lg bg-gray-100 p-4 text-center text-xs text-gray-500">
            <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
              <span>No image loaded</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-lg bg-gray-100 p-4 text-center text-xs text-gray-500">
        Loading image...
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <a
        href={proxyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 hover:underline text-xs"
      >
        View attachment {index + 1}
      </a>
    );
  }

  return (
    <div 
      className="rounded-lg overflow-hidden cursor-pointer"
      onClick={handleClick}
      onContextMenu={(e) => e.preventDefault()}
    >
      <img
        src={imageSrc}
        alt={`Attachment ${index + 1}`}
        className="max-w-full h-auto rounded-lg hover:opacity-90 transition-opacity"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
      />
    </div>
  );
}

